import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import AttendanceHeaderButton from './AttendanceHeaderButton';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FolderOpen,
  Settings,
  FileText,
  BarChart3,
  Building2,
  AlertCircle,
  Mail,
  ClipboardList,
  Handshake,
  Library,
  Inbox,
  Send,
  FileEdit,
  Calculator,
  DollarSign,
  Receipt,
  PieChart,
  ChevronDown,
  ChevronRight,
  GripVertical,
  MessageSquare,
  Check,
  X,
  Megaphone,
  TrendingUp,
  Upload,
  History,
  Target,
  Clock,
  UserCheck,
  Flame,
  Layers,
  UserCog,
  Tag,
  ShieldAlert,
  BarChart2,
  Scale,
} from 'lucide-react';

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuOrder, setMenuOrder] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempMenuOrder, setTempMenuOrder] = useState(null);

  // Load saved menu order from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedOrder = localStorage.getItem(`menuOrder_${user.id}`);
      if (savedOrder) {
        try {
          setMenuOrder(JSON.parse(savedOrder));
        } catch (e) {
          console.error('Failed to parse menu order');
        }
      }
    }
  }, [user?.id]);

  // Auto-expand active submenus
  useEffect(() => {
    const newExpanded = { ...expandedMenus };
    getBaseMenuItems().forEach(item => {
      if (item.submenu) {
        const basePath = item.path.split('?')[0];
        const subPaths = item.submenu.map(s => s.path.split('?')[0]);
        const isActive = location.pathname.startsWith(basePath) ||
          subPaths.some(sp => location.pathname.startsWith(sp));
        if (isActive) {
          newExpanded[item.label] = true;
        }
      }
    });
    setExpandedMenus(newExpanded);
  }, [location.pathname]);

  const toggleSubmenu = (label, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const getBaseMenuItems = () => {
    switch (user?.role) {
      case 'super_admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 2,
            submenu: [
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Building2, label: 'Visa Programs', path: '/admin/visa-programs' },
              { icon: ClipboardList, label: 'Checklists', path: '/admin/checklists' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          {
            icon: UserCog,
            label: 'HR',
            path: '/admin/users',
            priority: 3,
            submenu: [
              { icon: Users, label: 'Staff', path: '/admin/users' },
            ]
          },
          { icon: Handshake, label: 'B2B Partners', path: '/admin/b2b-partners', priority: 4 },
          {
            icon: Calculator,
            label: 'Accounting',
            path: '/admin/accounting',
            priority: 5,
            submenu: [
              { icon: PieChart, label: 'Dashboard', path: '/admin/accounting' },
              { icon: DollarSign, label: 'Sales', path: '/admin/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/admin/accounting/expenses' },
              { icon: BarChart3, label: 'Reports', path: '/admin/accounting/reports' },
            ]
          },
          {
            icon: Mail,
            label: 'Email',
            path: '/admin/email',
            priority: 6,
            submenu: [
              { icon: Inbox, label: 'Inbox', path: '/admin/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/admin/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/admin/email?folder=drafts' },
              { icon: FileText, label: 'Templates', path: '/admin/email-templates' },
            ]
          },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 7 },
          { icon: MessageSquare, label: 'Complaint Types', path: '/admin/complaint-types', priority: 8 },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 9,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool' },
              { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources' },
            ]
          },
          {
            icon: TrendingUp,
            label: 'Sales',
            path: '/sales/manager',
            priority: 10,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
            ]
          },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 11,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
              { icon: BarChart2,       label: 'Analytics', path: '/legal/analytics' },
            ]
          },
          { icon: Settings, label: 'Settings', path: '/admin/settings', priority: 12 },
        ];
      case 'admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 2,
            submenu: [
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Building2, label: 'Visa Programs', path: '/admin/visa-programs' },
              { icon: ClipboardList, label: 'Checklists', path: '/admin/checklists' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          {
            icon: UserCog,
            label: 'HR',
            path: '/admin/users',
            priority: 3,
            submenu: [
              { icon: Users, label: 'Staff', path: '/admin/users' },
            ]
          },
          { icon: Handshake, label: 'B2B Partners', path: '/admin/b2b-partners', priority: 4 },
          {
            icon: Calculator,
            label: 'Accounting',
            path: '/admin/accounting',
            priority: 5,
            submenu: [
              { icon: PieChart, label: 'Dashboard', path: '/admin/accounting' },
              { icon: DollarSign, label: 'Sales', path: '/admin/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/admin/accounting/expenses' },
              { icon: BarChart3, label: 'Reports', path: '/admin/accounting/reports' },
            ]
          },
          {
            icon: Mail,
            label: 'Email',
            path: '/admin/email',
            priority: 6,
            submenu: [
              { icon: Inbox, label: 'Inbox', path: '/admin/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/admin/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/admin/email?folder=drafts' },
              { icon: FileText, label: 'Templates', path: '/admin/email-templates' },
            ]
          },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 7 },
          { icon: MessageSquare, label: 'Complaint Types', path: '/admin/complaint-types', priority: 8 },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 9,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool' },
              { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources' },
            ]
          },
          {
            icon: TrendingUp,
            label: 'Sales',
            path: '/sales/manager',
            priority: 10,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
            ]
          },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 11,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
              { icon: BarChart2,       label: 'Analytics', path: '/legal/analytics' },
            ]
          },
          { icon: Settings, label: 'Settings', path: '/admin/settings', priority: 12 },
        ];
      case 'process_manager':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 2,
            submenu: [
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Building2, label: 'Visa Programs', path: '/admin/visa-programs' },
              { icon: ClipboardList, label: 'Checklists', path: '/admin/checklists' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          { icon: UserCog, label: 'My Team', path: '/manager/officers', priority: 3 },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 4 },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 5,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool' },
            ]
          },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
            ]
          },
        ];
      case 'case_officer':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/officer', priority: 1 },
          { icon: Briefcase, label: 'My Cases', path: '/officer/cases', priority: 2 },
          { icon: FolderOpen, label: 'Documents', path: '/officer/documents', priority: 3 },
          { icon: Library, label: 'Document Library', path: '/officer/document-library', priority: 4 },
          { 
            icon: Mail, 
            label: 'Email', 
            path: '/officer/email',
            priority: 5,
            submenu: [
              { icon: Inbox, label: 'Inbox', path: '/officer/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/officer/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/officer/email?folder=drafts' },
            ]
          },
          { icon: AlertCircle, label: 'Complaints', path: '/officer/complaints', priority: 6 },
        ];
      case 'accountant':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/accountant', priority: 1 },
          { 
            icon: Calculator, 
            label: 'Accounting', 
            path: '/accountant/accounting',
            priority: 2,
            submenu: [
              { icon: PieChart, label: 'Overview', path: '/accountant/accounting' },
              { icon: DollarSign, label: 'Sales', path: '/accountant/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/accountant/accounting/expenses' },
              { icon: BarChart3, label: 'Reports', path: '/accountant/accounting/reports' },
            ]
          },
          { 
            icon: Mail, 
            label: 'Email', 
            path: '/accountant/email',
            priority: 3,
            submenu: [
              { icon: Inbox, label: 'Inbox', path: '/accountant/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/accountant/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/accountant/email?folder=drafts' },
            ]
          },
        ];
      case 'client':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/client', priority: 1 },
          { icon: FileText, label: 'My Application', path: '/client/application', priority: 2 },
          { icon: FolderOpen, label: 'Documents', path: '/client/documents', priority: 3 },
          { icon: AlertCircle, label: 'Complaints', path: '/client/complaints', priority: 4 },
        ];
      case 'sales_consultant':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/sales', priority: 1 },
          { icon: Target, label: 'My Leads', path: '/sales/leads', priority: 2 },
          { icon: Clock, label: 'Attendance', path: '/sales/attendance', priority: 3 },
        ];
      case 'sales_manager':
        return [
          { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager', priority: 1 },
          { icon: Target, label: 'Pipeline', path: '/sales/manager/leads', priority: 2 },
          { icon: UserCheck, label: 'My Team', path: '/sales/manager/leaderboard', priority: 3 },
          { icon: BarChart3, label: 'Reports', path: '/sales/reports', priority: 4 },
        ];
      case 'marketing_manager':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/marketing', priority: 1 },
          { icon: Upload, label: 'Upload Leads', path: '/admin/marketing/upload', priority: 2 },
          { icon: History, label: 'Import History', path: '/admin/marketing/history', priority: 3 },
          { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool', priority: 4 },
          { icon: TrendingUp, label: 'Analytics', path: '/admin/marketing/analytics', priority: 5 },
          { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources', priority: 6 },
          {
            icon: Target,
            label: 'Sales Insights',
            path: '/sales/manager',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: UserCheck, label: 'Leaderboard', path: '/sales/manager/leaderboard' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
            ]
          },
        ];
      case 'legal_counsel':
        return [
          { icon: LayoutDashboard, label: 'Dashboard',   path: '/legal',                    priority: 1 },
          { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations',         priority: 2 },
          { icon: BarChart2,       label: 'Analytics',   path: '/legal/analytics',           priority: 3 },
        ];
      default:
        return [];
    }
  };

  // Apply saved order or use default
  const getMenuItems = () => {
    const baseItems = getBaseMenuItems();
    if (menuOrder && user?.role !== 'client') {
      // Sort by saved order
      return [...baseItems].sort((a, b) => {
        const orderA = menuOrder[a.label] ?? a.priority;
        const orderB = menuOrder[b.label] ?? b.priority;
        return orderA - orderB;
      });
    }
    return baseItems;
  };

  // Drag and drop handlers with visual feedback
  const [dragOverItem, setDragOverItem] = useState(null);
  
  // Edit mode functions
  const handleEnterEditMode = () => {
    setTempMenuOrder(menuOrder ? { ...menuOrder } : null);
    setIsEditMode(true);
  };

  const handleSaveOrder = () => {
    // tempMenuOrder already has the latest order from drag operations
    if (tempMenuOrder) {
      setMenuOrder(tempMenuOrder);
      localStorage.setItem(`menuOrder_${user.id}`, JSON.stringify(tempMenuOrder));
    }
    setIsEditMode(false);
    setTempMenuOrder(null);
  };

  const handleCancelEdit = () => {
    setTempMenuOrder(null);
    setIsEditMode(false);
    setDraggedItem(null);
    setDragOverItem(null);
  };
  
  const handleDragStart = (e, item) => {
    if (!isEditMode) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    if (!isEditMode || !draggedItem || draggedItem.label === item.label) return;
    setDragOverItem(item.label);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    setDragOverItem(null);
    if (!isEditMode || !draggedItem || draggedItem.label === targetItem.label) return;

    const items = getMenuItems();
    const dragIndex = items.findIndex(i => i.label === draggedItem.label);
    const targetIndex = items.findIndex(i => i.label === targetItem.label);

    // Create new order
    const newOrder = {};
    const reorderedItems = [...items];
    reorderedItems.splice(dragIndex, 1);
    reorderedItems.splice(targetIndex, 0, draggedItem);
    
    reorderedItems.forEach((item, index) => {
      newOrder[item.label] = index + 1;
    });

    // Store in temp order (will be saved when clicking Save)
    setTempMenuOrder(newOrder);
    setMenuOrder(newOrder);  // Update display immediately
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const menuItems = getMenuItems();
  const canEditMenu = user?.role !== 'client';

  return (
    <>
    {/* Global fixed top header — appears on every page */}
    <div
      className="fixed top-0 right-0 z-40 flex items-center justify-end gap-2 px-5 bg-white border-b border-slate-200"
      style={{ left: '260px', height: '56px' }}
      data-testid="global-top-header"
    >
      {user?.role !== 'client' && <AttendanceHeaderButton />}
      <div className="w-px h-6 bg-slate-200" />
      <NotificationBell />
      <ProfileMenu />
    </div>

    <div className="sidebar group/sidebar" data-testid="sidebar">
      <div className="p-4 border-b border-slate-200 flex items-center justify-center">
        <img src="/immiways-logo.png" alt="Immiways" className="h-20 w-auto object-contain" data-testid="sidebar-logo" />
      </div>

      {/* Edit Mode Banner - Only shows when in edit mode */}
      {isEditMode && (
        <div className="px-3 py-2 bg-teal-50 border-b border-teal-200 flex items-center justify-between">
          <span className="text-xs font-medium text-teal-700">Drag to reorder</span>
          <div className="flex items-center gap-1">
            <button
              onClick={handleSaveOrder}
              className="p-1.5 rounded-md bg-teal-500 hover:bg-teal-600 text-white transition-colors"
              title="Save"
              data-testid="save-menu-order"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancelEdit}
              className="p-1.5 rounded-md bg-white hover:bg-slate-100 text-slate-600 transition-colors border border-slate-200"
              title="Cancel"
              data-testid="cancel-edit-menu"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="p-3 flex-1 overflow-y-auto">
        <nav className="space-y-1" data-testid="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            
            // Improved active state detection
            const basePath = item.path.split('?')[0];
            const isDashboard = basePath === '/admin' || basePath === '/manager' || basePath === '/officer' || basePath === '/client' || basePath === '/accountant';
            
            let isActive = false;
            if (isDashboard) {
              isActive = location.pathname === basePath;
            } else {
              isActive = location.pathname === basePath || location.pathname.startsWith(basePath + '/');
            }
            
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus[item.label];
            
            return (
              <div 
                key={item.path}
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => handleDragOver(e, item)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
                onDragEnd={handleDragEnd}
                className={`
                  transition-all duration-200 rounded-lg
                  ${draggedItem?.label === item.label ? 'opacity-50 scale-95 bg-slate-100' : ''}
                  ${dragOverItem === item.label && isEditMode ? 'border-t-2 border-teal-500 bg-teal-50' : 'border-t-2 border-transparent'}
                `}
              >
                <div className="flex items-center">
                  {isEditMode && (
                    <div className="cursor-grab active:cursor-grabbing pr-1 text-teal-500">
                      <GripVertical size={14} />
                    </div>
                  )}
                  <Link
                    to={hasSubmenu ? '#' : item.path}
                    onClick={(e) => {
                      if (hasSubmenu) {
                        toggleSubmenu(item.label, e);
                      }
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className={isActive ? 'text-white' : 'text-slate-600'} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {hasSubmenu && (
                      <div 
                        onClick={(e) => toggleSubmenu(item.label, e)}
                        className={`p-1 rounded hover:bg-white/10 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                      >
                        <ChevronDown size={16} className={isActive ? 'text-white' : 'text-slate-500'} />
                      </div>
                    )}
                  </Link>
                </div>
                
                {/* Submenu items with smooth animation */}
                {hasSubmenu && (
                  <div
                    className={`ml-8 mt-1 space-y-1 overflow-hidden transition-all duration-200 ${
                      isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {item.submenu.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const subBasePath = subItem.path.split('?')[0];
                      const isSubActive = location.pathname === subBasePath || 
                        (subItem.path.includes('?') && location.search.includes(subItem.path.split('?')[1]));
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                            isSubActive
                              ? 'bg-slate-800 text-white'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                          }`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Footer with copyright and hover-reveal customize button */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center justify-center">
          <p className="text-xs text-slate-400">© 2026 Immiways</p>
          {canEditMenu && !isEditMode && (
            <button
              onClick={handleEnterEditMode}
              className="ml-2 p-1 rounded-md text-slate-300 hover:text-slate-500 hover:bg-slate-100 opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200"
              title="Customize menu order"
              data-testid="edit-menu-order"
            >
              <Settings size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
